import { ArrowDown, ArrowUp } from "lucide-react";
import { useEffect, useState } from "react";

import { useStrings } from "../../i18n/index.tsx";
import { COL, edgeOf, FOCUS } from "../../theme.ts";
import { reducedMotion } from "../motion.ts";

interface JumpButtonProps {
	target: HTMLElement | null;
	onJump?: (direction: "up" | "down") => void;
}

export function JumpButton({ target, onJump }: JumpButtonProps) {
	const t = useStrings();
	const [away, setAway] = useState<"up" | "down" | null>(null);
	useEffect(() => {
		// Absent in some non-browser hosts: no button there.
		if (!target || typeof IntersectionObserver !== "function") return;
		const io = new IntersectionObserver(
			([entry]) => {
				if (!entry) return;
				setAway(
					entry.isIntersecting
						? null
						: entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0)
							? "up"
							: "down",
				);
			},
			// The sticky header (72) and the Nav with its padding (128) cover the viewport's ends.
			{ rootMargin: "-72px 0px -128px 0px" },
		);
		io.observe(target);
		return () => {
			io.disconnect();
			setAway(null);
		};
	}, [target]);
	if (!away || !target) return null;
	const Arrow = away === "up" ? ArrowUp : ArrowDown;
	return (
		<div className="pointer-events-none fixed inset-x-0 bottom-28 mx-auto flex max-w-md justify-end px-4">
			<button
				type="button"
				data-testid="path-jump"
				aria-label={t("home.jumpToPlace")}
				onClick={() => {
					target.scrollIntoView?.({
						block: "center",
						behavior: reducedMotion() ? "auto" : "smooth",
					});
					target.querySelector("button")?.focus({ preventScroll: true });
					onJump?.(away);
				}}
				className={`motion-jump pointer-events-auto flex items-center justify-center rounded-full active:translate-y-1 ${FOCUS}`}
				style={{
					width: 52,
					height: 52,
					background: COL.card,
					border: `2px solid ${COL.line}`,
					boxShadow: `0 4px 0 ${edgeOf(COL.line)}`,
					color: COL.pom,
				}}
			>
				<Arrow size={24} strokeWidth={3} />
			</button>
		</div>
	);
}
